/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap } from 'gsap';
import { parse } from 'query-string';
import { AmbientLight, Object3D, PerspectiveCamera, Scene, Vector2, Vector3 } from 'three';

import { FloorGameTierLabel } from '@pkg/types/floorGame';

import type Renderer from '../../globals/Renderer';

import { FloorGameWebGLVariant } from '../../types';
import PerformanceProfile from '../../utils/PerformanceProfile';
import SoundController from '../../utils/SoundController';

import { map } from './../../utils/MathUtils';
import CryptoLogo from './FloorGameUp/CardCryptoLogo';
import FloorText from './FloorGameUp/CardFloorText';
import Particles from './FloorGameUp/CardParticles';
import EndText from './FloorGameUp/EndText';
import Sparkles from './FloorGameUp/Sparkles';
import { soundTimeline } from './FloorGameUp/soundTimeline';
import { CardGradientBackground } from './Global';

interface CardFgUpOptions {
    type: string;
    tier: FloorGameTierLabel;
    name: string;
    image: string;
    floorPrice: number;
    collectionName?: string;
    isCollectionView?: boolean;
    nftDetailView?: boolean;
}

export default class CardGL extends Object3D {
    renderer: Renderer;
    scene: Scene;

    name: string;
    imageURL: string;
    floorPrice: number;
    collectionName: string;
    isCollectionView: boolean;

    // TODO: These need to be explicitly set within the constructor.
    // Otherwise, it needs to accept `undefined`.
    background: CardGradientBackground;
    cryptoLogo: CryptoLogo;
    floorText: FloorText;
    particles: Particles;
    sparkles: Sparkles[];
    endText: EndText;
    soundController: SoundController;

    tier = 0;
    time = 0;
    width = 1;
    height = 1;

    mouse = new Vector2(0, 0);
    mouseTarget = new Vector2(0, 0);
    mouseEase = 0.01;

    camera = new PerspectiveCamera(30, 1 / 1, 0.01, 1000);
    lookAtTarget = new Vector3(0, 0, 2);

    constructor(
        renderer: Renderer,
        { type, tier, name, image, floorPrice, collectionName, isCollectionView, nftDetailView }: CardFgUpOptions,
    ) {
        super();

        this.renderer = renderer;
        this.scene = new Scene();

        this.type = type;
        this.name = name;
        this.imageURL = image;

        this.floorPrice = floorPrice;
        this.collectionName = collectionName ?? '';
        this.isCollectionView = Boolean(isCollectionView);

        if (tier === FloorGameTierLabel.Tier2) {
            this.tier = 1;
        } else if (tier === FloorGameTierLabel.Tier3) {
            this.tier = 2;
        } else if (tier === FloorGameTierLabel.Tier4 || tier === FloorGameTierLabel.Tier5) {
            this.tier = 3;
        }

        if (Object.prototype.hasOwnProperty.call(parse(location.search), 'tier')) {
            this.tier = parseFloat(parse(location.search).tier) - 1;
        }

        const light = new AmbientLight(0xffffff);
        this.scene.add(light);

        this.camera.position.z = 5;

        this.scene.add(this);

        this.initGradientBackground();
        this.initCryptoLogo();
        this.initTextAnimation();
        this.initParticles();
        this.initSparkles();
        this.initEndText(Boolean(nftDetailView));
        this.initSound();

        if (PerformanceProfile.devMode) this.addGUI();
    }

    initGradientBackground() {
        this.background = new CardGradientBackground({
            tier: this.tier,
            type: FloorGameWebGLVariant.TierUp,
        });
        this.add(this.background);
    }

    initCryptoLogo() {
        this.cryptoLogo = new CryptoLogo({
            type: 'eth',
            tier: this.tier,
            imageURL: this.imageURL,
            isCollectionView: this.isCollectionView,
        });
        this.add(this.cryptoLogo);
        this.cryptoLogo.position.z = 2;
        this.cryptoLogo.position.y = 0.1;
    }

    initTextAnimation() {
        this.floorText = new FloorText({
            tier: this.tier,
            card: this,
            isCollectionView: this.isCollectionView,
            floorPrice: this.floorPrice,
        });
        this.add(this.floorText);
        this.floorText.position.z = 3;
    }

    initParticles() {
        this.particles = new Particles({
            tier: this.tier,
            isCollectionView: this.isCollectionView,
        });
        this.add(this.particles);
    }

    initSparkles() {
        this.sparkles = [];

        for (let i = 0; i < 5; i++) {
            const sparkles = new Sparkles({
                tier: this.tier,
                type: Math.min(i, 2),
                camera: this.camera,
            });

            sparkles.position.z = 3 + 0.01 * i;

            if (this.isCollectionView) sparkles.position.z += 0.5;

            this.add(sparkles);
            this.sparkles.push(sparkles);
        }

        this.sparkles[3].position.x -= 0.2;
        this.sparkles[3].position.y -= 0.15;

        this.sparkles[4].position.x += 0.15;
        this.sparkles[4].position.y += 0.2;
        this.sparkles[4].position.z -= -0.1;
    }

    initEndText(isDetailView) {
        this.endText = new EndText({
            tier: this.tier,
            floorPrice: this.floorPrice,
            collectionName: this.collectionName,
            isCollectionView: this.isCollectionView,
        });

        this.add(this.endText);

        if (isDetailView) {
            this.endText.position.x = 0.6;
            this.endText.position.y = 0.2;
            this.endText.position.z = 1;
        } else {
            this.endText.position.x = 0;
            this.endText.position.y = -0.5;
            this.endText.position.z = 1.5;
        }
    }

    initSound() {
        this.soundController = new SoundController(soundTimeline, '/assets/webgl/audio/floor-game-up/');
    }

    // setDomElement(domElement) {
    //     if (this.domElement) {
    //         this.domElement.removeEventListener('mousemove', this.onMouseMove);
    //         this.domElement.removeEventListener('mouseleave', this.onMouseLeave);
    //     }
    //
    //     this.domElement = domElement;
    //     this.domElement.addEventListener('mousemove', this.onMouseMove.bind(this));
    //     this.domElement.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    // }

    onMouseMove(event) {
        if (!this.layer.visible) return;

        this.mouseEase = 0.03;
        this.mouseTarget.x = map(
            event.clientX,
            this.layer.position.x,
            this.layer.position.x + this.layer.width,
            -1,
            1,
            true,
        );
        this.mouseTarget.y = map(
            event.clientY,
            -this.layer.position.y,
            -this.layer.position.y + this.layer.height,
            -1,
            1,
            true,
        );
    }

    onMouseLeave() {
        if (!this.layer.visible) return;

        this.mouseEase = 0.01;
        this.mouseTarget.x = 0;
        this.mouseTarget.y = 0;
    }

    setSize(width: number, height: number) {
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        if (this.cryptoLogo) this.cryptoLogo.setResolution(width, height);
        if (this.floorText) this.floorText.setPosition(width, height);
        if (this.endText) this.endText.setPosition(width, height);
    }

    animateIn() {
        this.canInteract = false;
        this.mouse.x = 0.0;
        this.mouse.y = 0.0;
        this.mouseTarget.x = 0.0;
        this.mouseTarget.y = 0.0;

        gsap.fromTo(
            this.layer.material.uniforms.uTransition,
            {
                value: 0,
            },
            {
                value: 1.0,
                duration: 2.3,
                delay: 1.0,
                // duration: 0.4,
                // delay: 3.3,
                ease: 'power2.in',
            },
        );

        this.background.animateIn(3);
        this.floorText.animateIn();
        this.cryptoLogo.animateIn();
        this.particles.animateIn();
        this.sparkles[0].animateIn(3.5, true);
        this.sparkles[1].animateIn(6);
        this.sparkles[2].animateIn(7.5);
        this.endText && this.endText.animateIn(5);
        this.sparkles[3].animateIn(8.5);
        this.sparkles[4].animateIn(10);

        this.soundController.playTimeline();

        setTimeout(() => {
            this.canInteract = true;
        }, 4000);
    }

    // animateOut() {
    // console.log('out');
    //}

    update(elapsed: number, delta: number) {
        this.time += 0.01;

        if (this.background) this.background.update(elapsed, delta);

        if (this.cryptoLogo) this.cryptoLogo.update(elapsed, delta);

        // if (this.floorText) this.floorText.update(elapsed, delta);

        if (this.particles) this.particles.update(elapsed, delta);

        // Parallax Effect
        if (this.canInteract) this.mouse.lerp(this.mouseTarget, this.mouseEase);

        this.camera.position.x = this.mouse.x;
        this.camera.position.y = this.mouse.y * -2.0;
        this.camera.lookAt(this.lookAtTarget);
    }

    delete() {
        if (this.domElement) {
            this.domElement.removeEventListener('mousemove', this.onMouseMove);
            this.domElement.removeEventListener('mouseleave', this.onMouseLeave);
        }

        this.remove(this.background);
        this.background.delete();
        this.remove(this.cryptoLogo);
        this.cryptoLogo.delete();
        this.remove(this.floorText);
        this.floorText.delete();
        this.remove(this.particles);
        this.particles.delete();

        this.sparkles.forEach((sparkle) => {
            this.remove(sparkle);
            sparkle.delete();
        });

        if (this.endText) this.endText.delete();

        this.soundController.killTimeline();

        if (PerformanceProfile.devMode) {
            this.GUI.dispose();
        }
    }

    addGUI() {
        this.GUI = PerformanceProfile.GUI.floorGameUp.addFolder({
            title: `${this.name} Card:`,
            expanded: false,
        });

        this.GUI.addButton({
            title: 'Animate In',
        }).on('click', () => {
            this.animateIn(this.type);
        });

        /*
        this.GUI.addButton({
            title: 'Animation Out'
        }).on('click', this.animateOut.bind(this));
        */
    }
}
